import React, {
  createContext,
  ReactElement,
  ComponentType,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import type { ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch';

interface ZoomControlsProps {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  [key: string]: any;
}

interface WithZoomViewerProps {
  children?: ReactElement;
  zoomControls?: ComponentType<ZoomControlsProps>;
  isAddAnnotation?: boolean;
  className?: string;
  [key: string]: any;
}

type NativeZoomConfig = {
  enabled: boolean;
  /** CSS scale that fits the natural-size bitmap into the viewer. */
  fitScale: number;
};

type ZoomNativeConfig = {
  nativeZoom: NativeZoomConfig;
  setNativeZoom: (config: NativeZoomConfig) => void;
  /** Current CSS zoom. 1 = window fit, 1 / fitScale = actual size. */
  viewScale: number;
};

const DEFAULT_MAX_SCALE = 8;
const ACTUAL_SIZE_SCALE = 1;

const DISABLED_NATIVE_ZOOM: NativeZoomConfig = {
  enabled: false,
  fitScale: 1,
};

const ZoomNativeConfigContext = createContext<ZoomNativeConfig>({
  nativeZoom: DISABLED_NATIVE_ZOOM,
  setNativeZoom: () => {},
  viewScale: 1,
});

export const useZoomNativeConfig = () => useContext(ZoomNativeConfigContext);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type WithZoomViewerHOC = <P extends object = object>(WrappedComponent: ComponentType<P>) => ComponentType<WithZoomViewerProps & P>;

const withZoomViewer: WithZoomViewerHOC = <P extends object = object>(WrappedComponent: ComponentType<P>) => {
  const WithZoomViewerComponent: React.FC<WithZoomViewerProps & P> = ({
    children,
    zoomControls: ZoomControls,
    isAddAnnotation,
    className = '',
    ...props
  }) => {
    const transformRef = useRef<ReactZoomPanPinchContentRef>(null);
    const lastFitScaleRef = useRef<number | null>(null);
    const [nativeZoom, setNativeZoomState] = useState<NativeZoomConfig>(DISABLED_NATIVE_ZOOM);
    const [viewScale, setViewScale] = useState(1);

    const setNativeZoom = useCallback((config: NativeZoomConfig) => {
      setNativeZoomState(previous => {
        const fitScale = Number.isFinite(config.fitScale) && config.fitScale > 0 ? config.fitScale : 1;
        const next = { enabled: Boolean(config.enabled), fitScale };
        if (previous.enabled === next.enabled && Math.abs(previous.fitScale - next.fitScale) < 0.001) {
          return previous;
        }
        return next;
      });
    }, []);

    const zoomConfig = useMemo(
      () => ({
        nativeZoom,
        setNativeZoom,
        viewScale,
      }),
      [nativeZoom, setNativeZoom, viewScale]
    );

    const useNativeImageZoom = nativeZoom.enabled && nativeZoom.fitScale > 0 && nativeZoom.fitScale <= 1;
    const minScale = 1;
    const maxScale = useNativeImageZoom ? Math.max(ACTUAL_SIZE_SCALE / nativeZoom.fitScale, 1) : DEFAULT_MAX_SCALE;
    const initialScale = 1;
    const zoomStep = useNativeImageZoom ? Math.max((maxScale - minScale) / 6, 0.05) : 0.5;

    useLayoutEffect(() => {
      if (!useNativeImageZoom) {
        lastFitScaleRef.current = null;
        setViewScale(1);
        return;
      }

      const shouldRefit =
        lastFitScaleRef.current === null || Math.abs(lastFitScaleRef.current - nativeZoom.fitScale) > 0.05;

      if (!shouldRefit) {
        return;
      }

      lastFitScaleRef.current = nativeZoom.fitScale;
      setViewScale(1);
      transformRef.current?.centerView(1, 0);
    }, [nativeZoom.fitScale, useNativeImageZoom]);

    const zoomFromCenter = useCallback(
      (direction: 1 | -1) => {
        const controls = transformRef.current;
        const scale = controls?.instance?.transformState.scale;
        if (!controls || typeof scale !== 'number') {
          return;
        }

        const nextScale = clamp(Number((scale + direction * zoomStep).toFixed(4)), minScale, maxScale);
        if (nextScale === scale) {
          return;
        }

        controls.centerView(nextScale, 200);
      },
      [minScale, maxScale, zoomStep]
    );

    return (
      <ZoomNativeConfigContext.Provider value={zoomConfig}>
        <div className={`h-full w-full overflow-hidden ${className}`}>
          <TransformWrapper
            ref={transformRef}
            initialScale={initialScale}
            initialPositionX={0}
            initialPositionY={0}
            minScale={minScale}
            maxScale={maxScale}
            wheel={{ step: zoomStep, smoothStep: 0 }}
            doubleClick={{ disabled: Boolean(isAddAnnotation), mode: 'zoomIn', step: zoomStep }}
            panning={{
              disabled: false,
              allowLeftClickPan: !isAddAnnotation,
              allowMiddleClickPan: true,
              allowRightClickPan: true,
              velocityDisabled: true,
            }}
            limitToBounds={true}
            centerZoomedOut={true}
            centerOnInit={false}
            smooth={false}
            velocityAnimation={{ disabled: true }}
            alignmentAnimation={{ disabled: true, sizeX: 0, sizeY: 0 }}
            onTransformed={(_ref, state) => {
              if (typeof state?.scale === 'number') {
                setViewScale(state.scale);
              }
            }}>
            {({ resetTransform, zoomIn: _libraryZoomIn, zoomOut: _libraryZoomOut, ...restTransformProps }) => (
              <>
                <TransformComponent
                  wrapperClass="!h-full !w-full"
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                  contentStyle={
                    useNativeImageZoom
                      ? {
                          width: 'max-content',
                          height: 'max-content',
                        }
                      : {
                          width: '100%',
                          height: '100%',
                        }
                  }>
                  {children ? (
                    <div style={useNativeImageZoom ? { width: 'max-content', height: 'max-content' } : undefined}>
                      {React.cloneElement(children, { ...props })}
                    </div>
                  ) : (
                    <WrappedComponent {...(props as P)} />
                  )}
                </TransformComponent>

                {ZoomControls && (
                  <ZoomControls
                    {...props}
                    {...restTransformProps}
                    resetTransform={resetTransform}
                    zoomIn={() => zoomFromCenter(1)}
                    zoomOut={() => zoomFromCenter(-1)}
                  />
                )}
              </>
            )}
          </TransformWrapper>
        </div>
      </ZoomNativeConfigContext.Provider>
    );
  };

  WithZoomViewerComponent.displayName = `WithZoomViewer(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithZoomViewerComponent;
};

export default withZoomViewer;
